import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Droplets, Candy, Flower2, Wind, Package, Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { effectVibes } from '../data/products';
import { useProducts } from '../context/ProductsContext';

// ─────────────────────────────────────────────
// Shared category page layout
// ─────────────────────────────────────────────
const CategoryPage = ({
  categoryKey,
  title,
  eyebrow,
  description,
  accentColor,
  heroGradient,
  Icon,
  heroImage,
  onAddToCart,
}) => {
  const { products } = useProducts();
  const [activeEffect, setActiveEffect] = useState('All');
  const [sortBy, setSortBy] = useState('featured');

  const filtered = useMemo(() => {
    let list = products.filter(p => p.category === categoryKey);
    if (activeEffect !== 'All') list = list.filter(p => p.effect === activeEffect);
    if (sortBy === 'price-asc') list.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') list.sort((a, b) => b.price - a.price);
    if (sortBy === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [products, activeEffect, sortBy, categoryKey]);

  const allInCategory = products.filter(p => p.category === categoryKey);
  const avgRating = allInCategory.length
    ? (allInCategory.reduce((s, p) => s + p.rating, 0) / allInCategory.length).toFixed(1)
    : null;

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>

      {/* ── Hero ── */}
      <div style={{
        background: heroGradient || 'var(--forest)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '320px',
        display: 'flex',
        alignItems: 'flex-end',
      }}>
        {/* Background image */}
        {heroImage && (
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3) saturate(0.6)',
          }} />
        )}
        {/* Gradient overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(26,58,42,0.92) 0%, rgba(26,58,42,0.55) 100%)',
        }} />
        {/* Decorative circle */}
        <div style={{
          position: 'absolute', right: '-80px', top: '-80px',
          width: '380px', height: '380px', borderRadius: '50%',
          background: `${accentColor}18`,
        }} />
        <div style={{
          position: 'absolute', left: '5%', bottom: '-40px',
          width: '180px', height: '180px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />

        <div style={{
          position: 'relative', zIndex: 2,
          maxWidth: '1280px', margin: '0 auto',
          padding: '64px 32px',
          width: '100%',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '24px',
        }}>
          <div>
            {/* Eyebrow */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: `${accentColor}22`,
              border: `1px solid ${accentColor}44`,
              borderRadius: '999px',
              padding: '5px 14px',
              marginBottom: '18px',
            }}>
              <Icon size={12} color={accentColor} />
              <span style={{
                fontSize: '0.7rem', fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                color: accentColor,
              }}>{eyebrow}</span>
            </div>

            <h1 style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(2.4rem, 5vw, 3.6rem)',
              color: 'var(--white)', letterSpacing: '-0.01em',
              lineHeight: '1.0', marginBottom: '14px',
            }}>{title}</h1>

            <p style={{
              color: 'rgba(255,255,255,0.55)',
              fontSize: '0.95rem', lineHeight: '1.7',
              maxWidth: '480px',
            }}>{description}</p>
          </div>

          {/* Stats pill */}
          {allInCategory.length > 0 && (
            <div style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '16px',
              padding: '20px 28px',
              display: 'flex', gap: '28px',
              backdropFilter: 'blur(10px)',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: 'DM Sans', fontWeight: 700, fontSize: '1.8rem',
                  color: 'var(--white)', lineHeight: 1,
                }}>{allInCategory.length}</div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Products</div>
              </div>
              {avgRating && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    fontFamily: 'DM Sans', fontWeight: 700, fontSize: '1.8rem',
                    color: 'var(--white)', lineHeight: 1,
                  }}>
                    <Star size={16} fill={accentColor} color={accentColor} />
                    {avgRating}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Avg Rating</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '36px 32px' }}>

        {/* Filter bar */}
        <div style={{
          display: 'flex', alignItems: 'center',
          gap: '10px', marginBottom: '32px',
          flexWrap: 'wrap',
          background: 'var(--white)',
          borderRadius: '16px',
          padding: '14px 20px',
          border: '1px solid var(--mist)',
        }}>
          <span style={{
            fontSize: '0.75rem', fontWeight: 700,
            color: 'var(--mid)', textTransform: 'uppercase',
            letterSpacing: '0.08em', marginRight: '4px',
          }}>Effect</span>

          {effectVibes.map(effect => (
            <button
              key={effect}
              onClick={() => setActiveEffect(effect)}
              style={{
                padding: '6px 16px', borderRadius: '999px',
                background: activeEffect === effect ? 'var(--forest)' : 'transparent',
                color: activeEffect === effect ? 'var(--white)' : 'var(--mid)',
                border: activeEffect === effect ? 'none' : '1.5px solid var(--mist)',
                fontFamily: 'Syne', fontWeight: 600, fontSize: '0.78rem',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              {effect}
            </button>
          ))}

          <div style={{ marginLeft: 'auto' }}>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                padding: '7px 14px', borderRadius: '999px',
                border: '1.5px solid var(--mist)',
                background: 'var(--smoke)',
                fontFamily: 'DM Sans', fontSize: '0.82rem',
                color: 'var(--charcoal)', cursor: 'pointer', outline: 'none',
              }}
            >
              <option value="featured">Featured</option>
              <option value="rating">Top Rated</option>
              <option value="price-asc">Price: Low–High</option>
              <option value="price-desc">Price: High–Low</option>
            </select>
          </div>
        </div>

        {/* Count */}
        <p style={{
          fontSize: '0.85rem', color: 'var(--mid)',
          marginBottom: '22px', fontWeight: 500,
        }}>
          Showing {filtered.length} product{filtered.length !== 1 ? 's' : ''}
          {activeEffect !== 'All' && <> · <span style={{ color: 'var(--forest)', fontWeight: 600 }}>{activeEffect}</span></>}
        </p>

        {/* Products grid */}
        {filtered.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '22px',
          }}>
            {filtered.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
              >
                <ProductCard product={product} onAddToCart={onAddToCart} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <h3 style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: '1.3rem',
              color: 'var(--charcoal)', marginBottom: '8px',
            }}>No products found</h3>
            <p style={{ color: 'var(--mid)', fontSize: '0.9rem', marginBottom: '20px' }}>
              Try a different effect filter
            </p>
            <button
              onClick={() => setActiveEffect('All')}
              style={{
                background: 'var(--forest)', color: 'var(--white)',
                border: 'none', borderRadius: '8px',
                padding: '12px 24px',
                fontFamily: 'Syne', fontWeight: 700, fontSize: '0.82rem',
                cursor: 'pointer', letterSpacing: '0.04em',
              }}
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Cross-sell: browse all */}
        <div style={{
          marginTop: '64px',
          background: 'var(--forest)',
          borderRadius: '24px',
          padding: '40px 40px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px',
        }}>
          <div>
            <p style={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '8px',
            }}>Explore More</p>
            <h3 style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: '1.5rem',
              color: 'var(--white)', letterSpacing: '-0.01em',
            }}>See the full collection</h3>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', marginTop: '6px' }}>
              {products.length} products · All third-party lab tested
            </p>
          </div>
          <Link
            to="/shop"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'var(--gold)', color: 'var(--white)',
              borderRadius: '10px', padding: '14px 28px',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.88rem',
              letterSpacing: '0.05em', textTransform: 'uppercase',
              textDecoration: 'none', transition: 'opacity 0.2s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Shop All <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Individual category exports
// ─────────────────────────────────────────────

export const DrinksPage = ({ onAddToCart }) => (
  <CategoryPage
    categoryKey="drinks"
    title="THC Drinks"
    eyebrow="Hemp-Derived · Lab Tested"
    description="Fast-acting, sparkling, and delicious. Our drinks deliver a precisely dosed experience in every sip — no calories, no compromise."
    accentColor="#4FC3F7"
    Icon={Droplets}
    heroImage="https://images.unsplash.com/photo-1622597467836-f3e3dce5d876?w=1400&q=80"
    onAddToCart={onAddToCart}
  />
);

export const GummiesPage = ({ onAddToCart }) => (
  <CategoryPage
    categoryKey="gummies"
    title="Gummies"
    eyebrow="Precisely Dosed · Vegan-Friendly"
    description="Every gummy is triple-tested for potency consistency. Pop one, trust the dose, feel the difference."
    accentColor="#CE93D8"
    Icon={Candy}
    heroImage="https://images.unsplash.com/photo-1621939514649-280e2ee25f60?w=1400&q=80"
    onAddToCart={onAddToCart}
  />
);

export const FlowerPage = ({ onAddToCart }) => (
  <CategoryPage
    categoryKey="flower"
    title="Hemp Flower"
    eyebrow="Indoor-Grown · THCA"
    description="Grown under controlled indoor conditions by CGMP-certified farms. Full terpene profiles, beautiful cures, and verified potency every time."
    accentColor="#81C784"
    Icon={Flower2}
    heroImage="https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?w=1400&q=80"
    onAddToCart={onAddToCart}
  />
);

export const VapesPage = ({ onAddToCart }) => (
  <CategoryPage
    categoryKey="vapes"
    title="Vapes"
    eyebrow="Ceramic Coil · Solvent-Free"
    description="Clean hardware meets premium distillate. Ceramic coils, no additives, full-panel tested for every batch."
    accentColor="#B39DDB"
    Icon={Wind}
    heroImage="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=1400&q=80"
    onAddToCart={onAddToCart}
  />
);

export const BundlesPage = ({ onAddToCart }) => (
  <CategoryPage
    categoryKey="bundles"
    title="Bundles"
    eyebrow="Save up to 15%"
    description="Curated combinations handpicked for the best all-around experience. Great for gifting, trying something new, or stocking up."
    accentColor="#c9a84c"
    Icon={Package}
    heroImage="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1400&q=80"
    onAddToCart={onAddToCart}
  />
);